<script setup lang="ts">
import { ref } from 'vue';
import type { Ride } from '../../types/ride';
import axios from 'axios';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success'): void;
}>();

const formData = ref({
  ride_type: 'offer',
  date: '',
  from: '',
  via: '',
  to: '',
  name: '',
  email: '',
  telefon: '',
});

const isSubmitting = ref(false);
const error = ref<string | null>(null);

async function handleSubmit() {
  try {
    isSubmitting.value = true;
    error.value = null;

    await axios.post('https://frappe.kry.bz/api/method/bmapp.utils.api.new_mitfahrt', formData.value);
    
    emit('success');
    emit('close');
  } catch (e) {
    error.value = 'Fehler beim Speichern der Mitfahrgelegenheit';
    console.error('Error creating ride:', e);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="modal-overlay" @click="emit('close')">
    <div class="modal-content" @click.stop>
      <h3>Neue Mitfahrgelegenheit</h3>

      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label>Art der Mitfahrgelegenheit</label>
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                v-model="formData.ride_type"
                value="offer"
                class="form-radio"
              >
              Mitfahrt anbieten
            </label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="formData.ride_type"
                value="request"
                class="form-radio"
              >
              Mitfahrer suchen
            </label>
          </div>
        </div>

        <div class="form-group">
          <label for="date">Datum</label>
          <input
            type="date"
            id="date"
            v-model="formData.date"
            required
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="from">Von</label>
          <input
            type="text"
            id="from"
            v-model="formData.from"
            required
            placeholder="Startort"
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="via">Über (optional)</label>
          <input
            type="text"
            id="via"
            v-model="formData.via"
            placeholder="Zwischenstopps"
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="to">Nach</label>
          <input
            type="text"
            id="to"
            v-model="formData.to"
            required
            placeholder="Zielort"
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model="formData.name"
            required
            placeholder="Ihr Name"
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="email">E-Mail</label>
          <input
            type="email"
            id="email"
            v-model="formData.email"
            required
            placeholder="ihre@email.de"
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="telefon">Telefon</label>
          <input
            type="tel"
            id="telefon"
            v-model="formData.telefon"
            required
            placeholder="+49"
            class="form-input"
          >
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div class="button-group">
          <button 
            type="button" 
            class="cancel-button"
            @click="emit('close')"
          >
            Abbrechen
          </button>
          <button 
            type="submit"
            class="submit-button"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Wird gespeichert...' : 'Speichern' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

h3 {
  color: #960040;
  margin-bottom: 1.5rem;
  text-align: center;
}

.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: #666;
  font-weight: 500;
}

.radio-group {
  display: flex;
  gap: 1rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.error-message {
  color: #dc2626;
  margin-bottom: 1rem;
  text-align: center;
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-button,
.submit-button {
  flex: 1;
  padding: 0.8rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button {
  background: #f3f4f6;
  color: #666;
}

.cancel-button:hover {
  background: #e5e7eb;
}

.submit-button {
  background: #960040;
  color: white;
}

.submit-button:hover:not(:disabled) {
  background: #7d0035;
}

.submit-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}
</style>